import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, MapPin, Building2, Calendar, CheckCircle, XCircle, AlertTriangle, Briefcase } from "lucide-react";
import API from "@/lib/api";

function SectionCard({ icon: Icon, title, children, color = "blue" }) {
  const colors = { blue: "border-blue-500/20 bg-blue-950/20", green: "border-emerald-500/20 bg-emerald-950/20", amber: "border-amber-500/20 bg-amber-950/20", slate: "border-slate-700 bg-slate-900" };
  return (
    <Card className={`${colors[color]} border`}>
      <CardHeader className="py-3 px-5 border-b border-slate-800/50">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-400" />{title}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5">{children}</CardContent>
    </Card>
  );
}

export default function SharedProposalView() {
  const { shareToken } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get(`/shared/proposal/${shareToken}`)
      .then(r => setData(r.data))
      .catch(e => {
        const status = e.response?.status;
        if (status === 404) setError("This link is no longer valid or has been revoked.");
        else if (status === 410) setError("This link has expired.");
        else setError("Unable to load proposal. Please contact the sender.");
      })
      .finally(() => setLoading(false));
  }, [shareToken]);

  if (loading) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
      <Card className="bg-slate-900 border-slate-800 max-w-md w-full">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Link Unavailable</h2>
          <p className="text-sm text-slate-400">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const { project, proposal, scope, pricing, branding } = data;
  const stages = [...new Set(scope.map(s => s.stage))];

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="shared-proposal-view">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center gap-4 mb-4">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-12 rounded-lg" />
            ) : (
              <div className="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
            )}
            <div>
              <h1 className="text-xl font-bold tracking-tight" data-testid="shared-proposal-title">
                {branding?.platform_name || "MEP .Engine"} — Fee Proposal
              </h1>
              <p className="text-xs text-slate-500">
                Version {proposal?.version || 1} {proposal?.issue_date ? `  |  Issued ${proposal.issue_date}` : ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        {/* Project Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Project</p>
            <p className="text-sm font-medium" data-testid="shared-project-name">{project?.name}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Client</p>
            <p className="text-sm font-medium">{project?.client_name}</p>
          </div>
          {project?.location && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Location</p>
              <p className="text-sm font-medium flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-500" />{project.location}</p>
            </div>
          )}
          {project?.sector && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Sector</p>
              <p className="text-sm font-medium flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-slate-500" />{project.sector}</p>
            </div>
          )}
          {project?.project_type && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Type</p>
              <p className="text-sm font-medium flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5 text-slate-500" />{project.project_type}</p>
            </div>
          )}
          {project?.disciplines?.length > 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Disciplines</p>
              <div className="flex gap-1.5 mt-1 flex-wrap">{project.disciplines.map(d => <Badge key={d} variant="outline" className="text-[10px] border-blue-800 text-blue-400">{d}</Badge>)}</div>
            </div>
          )}
        </div>

        {/* Project Overview */}
        {proposal?.project_overview && (
          <SectionCard icon={FileText} title="Project Overview" color="slate">
            <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{proposal.project_overview}</p>
          </SectionCard>
        )}

        {/* Scope of Services */}
        {scope.length > 0 && (
          <SectionCard icon={CheckCircle} title="Scope of Services" color="blue">
            {stages.map(stage => {
              const items = scope.filter(s => s.stage === stage);
              return (
                <div key={stage} className="mb-4 last:mb-0">
                  <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-2">{stage}</h4>
                  <div className="space-y-1.5">
                    {items.map((item, i) => (
                      <div key={item.id || `${stage}-${item.name}-${i}`} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" />
                        <div>
                          <span className="text-slate-200">{item.name}</span>
                          {item.discipline && <Badge variant="outline" className="ml-2 text-[9px] border-slate-700 text-slate-500">{item.discipline}</Badge>}
                          {item.description && <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </SectionCard>
        )}

        {/* Fee Summary */}
        {pricing && (
          <SectionCard icon={FileText} title="Fee Summary" color="green">
            <div className="space-y-4">
              {/* Total Fee */}
              <div className="text-center py-4 bg-emerald-950/30 rounded-lg border border-emerald-800/30">
                <p className="text-xs text-emerald-500 uppercase tracking-wider mb-1">Total Professional Fee</p>
                <p className="text-3xl font-mono font-bold text-white" data-testid="shared-total-fee">
                  {pricing.total_fee?.toLocaleString("en-GB", { style: "currency", currency: "GBP" })}
                </p>
                {pricing.discount_percentage > 0 && (
                  <p className="text-xs text-emerald-400 mt-1">Includes {pricing.discount_percentage}% discount ({pricing.discount_amount?.toLocaleString("en-GB", { style: "currency", currency: "GBP" })})</p>
                )}
              </div>

              {/* Fee by Stage */}
              {pricing.fee_by_stage && Object.keys(pricing.fee_by_stage).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Fee by Stage</h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800">
                        <TableHead className="text-xs text-slate-500">Stage</TableHead>
                        <TableHead className="text-xs text-slate-500 text-right">Fee</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(pricing.fee_by_stage).map(([stage, fee]) => (
                        <TableRow key={stage} className="border-slate-800/50">
                          <TableCell className="text-sm text-slate-300">{stage}</TableCell>
                          <TableCell className="text-sm text-right font-mono text-slate-200">{fee?.toLocaleString("en-GB", { style: "currency", currency: "GBP" })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Fee by Discipline */}
              {pricing.fee_by_discipline && Object.keys(pricing.fee_by_discipline).length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Fee by Discipline</h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800">
                        <TableHead className="text-xs text-slate-500">Discipline</TableHead>
                        <TableHead className="text-xs text-slate-500 text-right">Fee</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(pricing.fee_by_discipline).map(([disc, fee]) => (
                        <TableRow key={disc} className="border-slate-800/50">
                          <TableCell className="text-sm text-slate-300">{disc}</TableCell>
                          <TableCell className="text-sm text-right font-mono text-slate-200">{fee?.toLocaleString("en-GB", { style: "currency", currency: "GBP" })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Optional Services */}
              {pricing.optional_services?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Optional Services</h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800">
                        <TableHead className="text-xs text-slate-500">Service</TableHead>
                        <TableHead className="text-xs text-slate-500 text-right">Fee</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pricing.optional_services.map((opt, i) => (
                        <TableRow key={opt.id || `opt-${opt.name}-${i}`} className="border-slate-800/50">
                          <TableCell className="text-sm text-slate-300">{opt.name || opt.description}</TableCell>
                          <TableCell className="text-sm text-right font-mono text-slate-200">{opt.fee?.toLocaleString("en-GB", { style: "currency", currency: "GBP" })}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </SectionCard>
        )}

        {/* Assumptions */}
        {proposal?.assumptions?.length > 0 && (
          <SectionCard icon={AlertTriangle} title="Assumptions" color="amber">
            <ul className="space-y-1.5">
              {proposal.assumptions.map((a, i) => (
                <li key={`assumption-${i}`} className="flex items-start gap-2 text-sm text-slate-300">
                  <span className="text-amber-500 mt-0.5 shrink-0 text-xs font-mono">{i + 1}.</span>
                  <span>{typeof a === "string" ? a : a.text || a.description || JSON.stringify(a)}</span>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {/* Exclusions */}
        {proposal?.exclusions?.length > 0 && (
          <SectionCard icon={XCircle} title="Exclusions" color="slate">
            <ul className="space-y-1.5">
              {proposal.exclusions.map((e, i) => (
                <li key={`exclusion-${i}`} className="flex items-start gap-2 text-sm text-slate-400">
                  <XCircle className="w-3.5 h-3.5 text-slate-600 mt-0.5 shrink-0" />
                  <span>{typeof e === "string" ? e : e.text || e.description || JSON.stringify(e)}</span>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}

        {/* Footer */}
        <div className="pt-6 border-t border-slate-800 text-center space-y-2">
          <p className="text-xs text-slate-500">
            Prepared by {data?.shared_by || "MEP .Engine"}
            {data?.expires_at && <> &mdash; Link expires {new Date(data.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</>}
          </p>
          <p className="text-[10px] text-slate-600">
            {branding?.platform_name || "MEP .Engine"} &mdash; {branding?.company_name || "Engineering Consultancy Pricing Platform"}
          </p>
        </div>
      </div>
    </div>
  );
}
